import type { VoidFunctionComponent } from 'react';

import { BannerSize, BannerStyle } from '../../../../../../typings/enums';
import Banner from '../../../../generic/Banner';
import type { DependencyIssue } from '../../../../../../shared/dependenciesV2';

type Props = {
  issues: Array<Omit<DependencyIssue, 'notes'>>;
};

/**
 * Generates the issue detail page URL for a given issue
 * Strips off "/browse/dependencies" from the current path and appends the issue path.
 *
 * @param issueId - The ID of the issue to link to
 * @returns The URL path to the issue detail page
 */
const getIssueDetailPath = (issueId: number): string => {
  const currentPath = window.location.pathname;
  const parts = currentPath.split('/');
  const base = parts.slice(0, parts.length - 2).join('/');
  return `${base}/issues/vulnerability/${issueId}`;
};

/**
 * Displays a banner alerting users that a dependency has been flagged as malicious
 * Lists all malware reports associated with the dependency's issues, with links to view each issue
 * Only renders if there are issues with malwareReportName populated
 *
 * NOTE: This component currently assumes it's used on the dependencies page
 * If we ever reuse this component elsewhere, we'll need to pass in the full project base URL instead of relying on window.location.pathname.
 */
const MalwareIssueBanners: VoidFunctionComponent<Props> = ({ issues }) => {
  const malwareIssues = issues.filter((issue) => issue.malwareReportName);

  if (malwareIssues.length === 0) {
    return null;
  }

  const malwareList = malwareIssues
    .map((issue) => {
      const issueLink = getIssueDetailPath(issue.id);
      return `<li>${issue.malwareReportName} - <a href="${issueLink}" target="_blank">View Issue</a></li>`;
    })
    .join('');

  return (
    <div className="margin-bottom-md">
      <Banner
        title="Malicious Dependency"
        body={`<ul class="padding-left-xl">${malwareList}</ul>`}
        bannerStyle={BannerStyle.High}
        bannerSize={BannerSize.Medium}
      />
    </div>
  );
};

export default MalwareIssueBanners;
